import isEqual from 'lodash.isequal';
import { log } from './logger.js';
/**
 * This is the analytics class that handles all analytic call for all components
 */
export class AbstractAnalytics {
    constructor(config) {
        this.run = true;
        this.queue = [];
        this.errorCount = 0;
        this.config = {
            appId: config.appId || '',
            batch: config.batch !== undefined ? config.batch : true,
            batchSyncInterval: config.batchSyncInterval !== undefined ? config.batchSyncInterval : 10000,
            credentials: config.credentials !== undefined ? config.credentials : false,
            deduplicate: config.deduplicate !== undefined ? config.deduplicate : true,
            deploymentId: config.deploymentId || '',
            errorTolerance: config.errorTolerance !== undefined ? config.errorTolerance : 10,
            maxBatchItems: config.maxBatchItems !== undefined ? config.maxBatchItems : 50,
            token: config.token,
            url: config.url,
            kerberos: config.kerberos,
        };
        this.initializeInterval();
    }
    /**
     * log a feature of the library being used (put it on a queue and post it in interval)
     */
    track(options) {
        if (!this.run) {
            return;
        }
        const analyticsItem = this.generateAnalyticsItem(options);
        if (this.config.deduplicate) {
            this.addDeduplicatedEventInQueue(analyticsItem);
        }
        else {
            this.queue.push(analyticsItem);
        }
        if (this.config.batch === false) {
            this.flush();
        }
    }
    stop() {
        this.run = false;
        clearInterval(this.intervalId);
        this.intervalId = undefined;
    }
    start() {
        if (this.intervalId || this.config.batch === false) {
            return;
        }
        this.run = true;
        this.initializeInterval();
    }
    /**
     * Empties the queue. Should be called before clearing the interval, to ensure no data is getting lost.
     */
    async flush() {
        while (this.queue.length > 0) {
            const chunk = this.queue.splice(0, this.config.maxBatchItems);
            log('Chunk length', chunk.length, ' | Remaining queue length', this.queue.length);
            await this.doPost(chunk);
        }
    }
    /**
     * Takes input an event and if exact same event exists in queue, increase its count otherwise add it to queue
     */
    addDeduplicatedEventInQueue(event) {
        const duplicatedEventIndex = this.queue.findIndex(queuedEvent => event.event === queuedEvent.event &&
            // we don't want to compare the count prop on the object so we replace it with 0
            isEqual({ ...event.properties, count: 0 }, { ...queuedEvent.properties, count: 0 }) &&
            isEqual(event.context, queuedEvent.context));
        if (duplicatedEventIndex < 0) {
            event.properties.count = 1;
            this.queue.push(event);
        }
        else {
            this.queue[duplicatedEventIndex].properties.count++;
            this.queue[duplicatedEventIndex].timestamp = event.timestamp;
        }
    }
    generateAnalyticsItem(options) {
        const { properties, eventName } = options;
        return {
            properties: { ...properties, count: 1 },
            token: this.config.token,
            event: eventName || 'Unknown Event',
            timestamp: new Date().toISOString(),
            context: {
                host: {
                    hostname: properties.app.hostname || '',
                    port: properties.app.port || '',
                    host: properties.app.host || '',
                },
                application: {
                    deploymentId: this.config.deploymentId || '',
                    appId: this.config.appId || '',
                    ...(options ? options.customApplicationProperties : {}),
                },
                client: this.getClientConfig(),
                user: this.config.kerberos
                    ? {
                        kerberos: this.config.kerberos,
                    }
                    : undefined,
            },
        };
    }
    initializeInterval() {
        if (this.config.batch === false) {
            return;
        }
        this.intervalId = setInterval(() => {
            if (this.queue.length > 0) {
                const chunk = this.queue.splice(0, this.config.maxBatchItems);
                this.doPost(chunk);
            }
        }, this.config.batchSyncInterval || 10000);
    }
    async doPost(data) {
        try {
            await this.post(data);
        }
        catch (err) {
            // Stop running after a max number of errors have been hit
            this.errorCount += 1;
            const errorTolerance = this.config.errorTolerance != undefined ? this.config.errorTolerance : 10;
            log('AnalyticsQueue post error. Will try', errorTolerance - this.errorCount, 'more times');
            if (this.errorCount >= errorTolerance) {
                this.run = false;
                log('AnalyticsQueue stopped after hitting max errors');
            }
            throw err;
        }
    }
}
//# sourceMappingURL=abstract-analytics.js.map